/* eslint-disable */
import { analyticsUrl } from "@/lib/analyticsUrl";
import { UAParser } from "ua-parser-js";

export interface AnalyticsBase {
  userId?: string | null;
  type:
    | "play"
    | "play_update"
    | "play_array"
    | "share"
    | "page_view"
    | "play_button_click"
    | "play_start"
    | "play_start_array";
  playbackSessionId?: string | null;
  songId?: number | null;
  playlistId?: number | null;
  workId?: number | null;
  userPlaylistId?: number | null;
  durationSec?: number | null;
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  landingPage?: string | null;
}

export interface AnalyticsEnvironment {
  visitorId: string | null;
  isNewVisitor: boolean;
  deviceType: string;
  os: string;
  browser: string;
}

export type AnalyticsPayload = AnalyticsBase & AnalyticsEnvironment;

function getOrCreateVisitor(): {
  visitorId: string | null;
  isNewVisitor: boolean;
} {
  let isNewVisitor = false;
  let visitorId = localStorage.getItem("visitorId");

  if (!visitorId) {
    visitorId = `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    localStorage.setItem("visitorId", visitorId);
    isNewVisitor = true;
  }

  return { visitorId, isNewVisitor };
}

/**
 * Adds visitor/device/browser information to an analytics event.
 * This is the single place where the analytics environment is created.
 */
export function getAnalyticsPayload(
  base: AnalyticsBase,
): AnalyticsPayload {
  const { visitorId, isNewVisitor } = getOrCreateVisitor();

  const parser = new UAParser();

  const osName = parser.getOS().name || "Unknown";
  const osVersion = parser.getOS().version || "";

  const browserName = parser.getBrowser().name || "Unknown";
  const browserVersion = parser.getBrowser().version || "";

  return {
    ...base,
    userId: base.userId ?? null,

    visitorId,
    isNewVisitor,

    deviceType: parser.getDevice().type || "desktop",

    os: osVersion
      ? `${osName} ${osVersion}`
      : osName,

    browser: browserVersion
      ? `${browserName} ${browserVersion}`
      : browserName,
  };
}

/**
 * Builds only the event-specific analytics data.
 *
 * Visitor ID, device, OS and browser are added later by
 * getAnalyticsPayload().
 */
export function buildAnalyticsBase({
  session,
  currentTrack,
  currentPlaylistId,
  currentType,
  durationSec,
  referrer,
  utmSource,
  utmMedium,
  utmCampaign,
  landingPage,
}: {
  session: any;
  currentTrack: any;
  currentPlaylistId: number | null;
  currentType:
    | "playlist"
    | "work"
    | "userplaylist"
    | "song"
    | null;
  durationSec: number | null;
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  landingPage?: string | null;
}): AnalyticsBase {
  const base: AnalyticsBase = {
    userId: (session?.user as any)?.id ?? null,

    type: "play",

    durationSec,

    referrer: analyticsUrl(referrer, window.location.origin),

    utmSource: utmSource ?? null,
    utmMedium: utmMedium ?? null,
    utmCampaign: utmCampaign ?? null,

    landingPage: analyticsUrl(landingPage, window.location.origin),
  };

  if (currentPlaylistId != null) {
    if (currentType === "work") {
      base.workId = Number(currentPlaylistId);
    } else if (currentType === "userplaylist") {
      base.userPlaylistId = Number(currentPlaylistId);
    } else {
      base.playlistId = Number(currentPlaylistId);
    }
  } else if (currentTrack?.id) {
    base.songId = Number(currentTrack.id);

    if (currentTrack.workId != null) {
      base.workId = Number(currentTrack.workId);
    }
  }

  return base;
}

export async function logAnalyticsEvent(base: AnalyticsBase) {
  // All visitor/device/browser information is added here,
  // exactly once.
  const payload = getAnalyticsPayload(base);

  const cleanPayload: any = {
    type: payload.type,

    playbackSessionId: payload.playbackSessionId ?? null,

    durationSec: payload.durationSec ?? null,

    deviceType: payload.deviceType,
    os: payload.os,
    browser: payload.browser,
  };

  if (payload.referrer !== undefined) {
    cleanPayload.referrer = analyticsUrl(payload.referrer, window.location.origin);
  }

  if (payload.utmSource !== undefined) {
    cleanPayload.utmSource = payload.utmSource;
  }

  if (payload.utmMedium !== undefined) {
    cleanPayload.utmMedium = payload.utmMedium;
  }

  if (payload.utmCampaign !== undefined) {
    cleanPayload.utmCampaign = payload.utmCampaign;
  }

  if (payload.landingPage !== undefined) {
    cleanPayload.landingPage = analyticsUrl(payload.landingPage, window.location.origin);
  }

  if (!payload.userId) {
    cleanPayload.visitorId = payload.visitorId;
    cleanPayload.isNewVisitor = payload.isNewVisitor;
  }

  if (payload.songId) {
    cleanPayload.songId = payload.songId;
  } else if (payload.playlistId) {
    cleanPayload.playlistId = payload.playlistId;
  } else if (payload.workId) {
    cleanPayload.workId = payload.workId;
  } else if (payload.userPlaylistId) {
    cleanPayload.userPlaylistId = payload.userPlaylistId;
  }

  const data = JSON.stringify(cleanPayload);

  if (document.visibilityState === "hidden") {
    const success = navigator.sendBeacon("/api/hive", data);

    if (!success) {
      await fetch("/api/hive", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: data,
      });
    }

    return;
  }

  await fetch("/api/hive", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: data,
  });
}